/* Verbatim port of ownershipByTermLoc, primaryTermRows, searchHeadline,
 * peerRank from service/review/index.html (~975-1031).
 *
 * PARAMETERIZED (rule a): all four read the global `STATE` (directly, or
 * transitively via ownershipByTermLoc/primaryTermRows) — each now takes
 * `state: AppPayloadState` as an explicit first parameter.
 */
import { fixMojibake } from '../lib/format.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { Page1Mode, PeerEntry } from '../types/contract.ts';

/** One term × location page-1 ownership row (from IMPACT's measured tags). */
export interface TermLocRow {
  key: string;
  term: string;
  location: string;
  /** null when page-1 was not classified (partial coverage) — never fabricated. */
  owned: number | null;
  slots: number | null;
  negative: number | null;
  page1Mode?: Page1Mode;
  polledAt: string;
}

/** searchHeadline()'s two shapes: measured rows (best/worst/rows populated)
 * or the single-capture fallback (`single` set, best/worst null). */
export interface SearchHeadline {
  avg: number;
  best: TermLocRow | null;
  worst: TermLocRow | null;
  bestLocs?: string[];
  worstLocs?: string[];
  negLocs: string[];
  rows: TermLocRow[];
  single?: { owned: number; slots: number; location: string };
}

export interface PeerRankInfo {
  rank: number;
  of: number;
  sorted: PeerEntry[];
  client: PeerEntry;
  best: PeerEntry;
}

/* Search plane: page-1 ownership per term × location, straight from
   IMPACT's measured tags. Returns [{term, location, owned, slots, negative, polledAt}] */
export function ownershipByTermLoc(state: AppPayloadState): TermLocRow[] {
  const imp = state.impact;
  if (!imp || !imp.page1OwnershipByTerm) return [];
  return Object.entries(imp.page1OwnershipByTerm).map(([key, o]) => {
    const term = (fixMojibake(key).split('·')[0] ?? '').trim();
    // W1-B: preserve null (partial coverage → unmeasured) — never fabricate 10/0.
    // A legacy row (no page1Mode but a slots count) is the position-assumed fallback.
    return { key, term, location: o.location, owned: o.page1Owned ?? null, slots: o.page1Slots ?? null,
             negative: o.page1Negative ?? null,
             page1Mode: o.page1Mode ?? (o.page1Slots != null ? 'position-assumed' : undefined),
             polledAt: o.polledAt };
  });
}

/** W1-B finding 3: the aggregate page-1 classification mode across the run's SERP
 *  captures — what the Search page footer/caveat must state. 'reported' = IMPACT's
 *  own page data; 'position-assumed' = base-10 fallback; 'partial'/'mixed' = mixed. */
export function searchPage1Mode(state: AppPayloadState): Page1Mode {
  const caps = [state.serp, ...(state.serps || [])].filter(Boolean) as { page1Mode?: Page1Mode }[];
  const modes = new Set<Page1Mode>();
  for (const c of caps) modes.add(c.page1Mode || 'position-assumed');
  for (const r of ownershipByTermLoc(state)) modes.add(r.page1Mode || 'position-assumed');
  if (modes.has('partial')) return 'partial';
  const real = [...modes].filter((m) => m !== 'unavailable');
  if (!real.length) return 'unavailable';
  if (real.length === 1) return real[0]!;
  return real.every((m) => m === 'position-assumed') ? 'position-assumed'
    : real.every((m) => m === 'reported') ? 'reported' : 'mixed';
}
/* Locations for the primary term only (dedup: first term listed = display term). */
export function primaryTermRows(state: AppPayloadState) {
  const rows = ownershipByTermLoc(state);
  if (!rows.length) return [];
  const terms = [...new Set(rows.map(r => r.term))];
  const t = terms[0];
  return rows.filter(r => r.term === t);
}
export function searchHeadline(state: AppPayloadState): SearchHeadline | null {
  // W1-B: aggregate only MEASURED terms — a partial-coverage term (null counts)
  // is excluded, never coerced to 0 (which would fabricate an owned/negative count).
  const rows = primaryTermRows(state).filter(
    (r): r is TermLocRow & { owned: number; slots: number; negative: number } =>
      r.owned != null && r.slots != null && r.negative != null,
  );
  if (!rows.length){
    // fallback: judge from the single serp capture
    const s = state.serp; if (!s) return null;
    const p1 = s.results.filter((r) => r.page === 1);
    const owned = p1.filter((r) => r.ownership === 'owned').length;
    return { avg: owned, best: null, worst: null, negLocs: [], rows: [], single:{owned, slots:p1.length, location:s.location} };
  }
  const maxOwned = Math.max(...rows.map(r => r.owned));
  const minOwned = Math.min(...rows.map(r => r.owned));
  const best = rows.find(r => r.owned === maxOwned) ?? null;
  const worst = rows.find(r => r.owned === minOwned) ?? null;
  const bestLocs = rows.filter(r => r.owned === maxOwned).map(r => r.location);
  const worstLocs = rows.filter(r => r.owned === minOwned).map(r => r.location);
  const negLocs = rows.filter(r => r.negative > 0).map(r => r.location);
  const avg = Math.round(rows.reduce((s,r) => s + r.owned, 0) / rows.length * 10) / 10;
  return { avg, best, worst, bestLocs, worstLocs, negLocs, rows };
}

export function peerRank(state: AppPayloadState): PeerRankInfo | null {
  const p = state.peers; if (!p || !p.peers || !p.peers.length) return null;
  const sorted = p.peers.slice().sort((a,b) => b.selfCitationShare - a.selfCitationShare);
  const idx = sorted.findIndex((x) => x.isClient);
  const client = sorted[idx], best = sorted[0];
  return idx < 0 || !client || !best ? null : { rank: idx+1, of: sorted.length, sorted, client, best };
}
